// How an uploaded game is stored in PocketBase. See docs/pocketbase-migration-plan.md §4.
//
// One file, four users: the upload route in stcc_upload.pb.js (PocketBase's goja VM),
// migration/migrate.mjs (node), the app (via the rollup commonjs plugin) and
// src/stcc_split.test.mjs. So there is one definition of "lossless", and this file sticks
// to syntax goja is happy with: no spread, no optional chaining, no imports.
//
// A game document is split three ways, and every field goes to exactly one place:
//   summary  – everything except the heavy fields below; what the Stats list loads
//   details  – cards, prefs and each player's stats.turns; loaded on demand
//   blobs    – fullState and replayInfo, the compressed log and replay; stored as files
// reassemble(split(doc)) deep-equals doc. src/stcc_split.test.mjs checks that for every
// game in the export.

var HEAVY = ['cards', 'prefs'];
var BLOBS = ['fullState', 'replayInfo'];
// Card ids start with the box's ref (a digit, in practice) followed by one of these for
// commons. Mirrors the box filter in Stats.svelte, which only looks at commons.
var BOX_PREFIX = /^(.*?)(PER|CAR|SHI|ALL|LOC|ENC|INC)/;
var MAX_KEY_LENGTH = 200;

function hasOwn(o, k){
    return Object.prototype.hasOwnProperty.call(o, k);
}

function isPlainObject(o){
    return !!o && typeof o == 'object' && !Array.isArray(o);
}

function shallowCopy(o){
    var c = {};
    Object.keys(o).forEach(function(k){ c[k] = o[k]; });
    return c;
}

function checkKey(key){
    if(typeof key != 'string' || key.trim() == '') throw new Error('game has no key');
    if(key.length > MAX_KEY_LENGTH) throw new Error('game key is too long');
    return key;
}

function split(doc){
    if(!isPlainObject(doc)) throw new Error('game must be an object');
    checkKey(doc.key);
    var summary = {};
    var details = {missing: [], turns: {}};
    var blobs = {};
    Object.keys(doc).forEach(function(k){
        if(HEAVY.indexOf(k) >= 0){
            details[k] = doc[k];
        }else if(BLOBS.indexOf(k) >= 0){
            blobs[k] = doc[k];
        }else{
            summary[k] = doc[k];
        }
    });
    // Absent and null are different things; remember which heavy fields were absent.
    HEAVY.forEach(function(k){
        if(!hasOwn(doc, k)){
            details.missing.push(k);
            details[k] = null;
        }
    });
    if(Array.isArray(doc.players)){
        summary.players = doc.players.map(function(p, i){
            if(!isPlainObject(p) || !isPlainObject(p.stats) || !hasOwn(p.stats, 'turns')) return p;
            details.turns[String(i)] = p.stats.turns;
            var stats = shallowCopy(p.stats);
            delete stats.turns;
            var np = shallowCopy(p);
            np.stats = stats;
            return np;
        });
    }
    return {key: doc.key, summary: summary, details: details, blobs: blobs};
}

// parts: {summary, details, blobs?}. Blob values are put back as given.
function reassemble(parts){
    if(!parts || !isPlainObject(parts.summary)) throw new Error('reassemble: no summary');
    if(!isPlainObject(parts.details)) throw new Error('reassemble: no details for ' + parts.summary.key);
    var d = parts.details;
    var missing = Array.isArray(d.missing) ? d.missing : [];
    var doc = shallowCopy(parts.summary);
    HEAVY.forEach(function(k){
        if(missing.indexOf(k) < 0) doc[k] = d[k] === undefined ? null : d[k];
    });
    var turns = isPlainObject(d.turns) ? d.turns : {};
    var turnKeys = Object.keys(turns);
    if(turnKeys.length > 0){
        if(!Array.isArray(doc.players)) throw new Error('reassemble: turns but no players for ' + doc.key);
        doc.players = doc.players.slice();
        turnKeys.forEach(function(i){
            var p = doc.players[Number(i)];
            if(!isPlainObject(p) || !isPlainObject(p.stats)) throw new Error('reassemble: turns for missing player ' + i + ' in ' + doc.key);
            var np = shallowCopy(p);
            np.stats = shallowCopy(p.stats);
            np.stats.turns = turns[i];
            doc.players[Number(i)] = np;
        });
    }
    if(parts.blobs){
        BLOBS.forEach(function(k){
            if(parts.blobs[k] !== undefined && parts.blobs[k] !== null) doc[k] = parts.blobs[k];
        });
    }
    return doc;
}

// JSON with object keys sorted at every level, so equal data always gives equal text.
// Otherwise follows JSON.stringify: undefined object members are skipped, undefined array
// items and non-finite numbers become null.
function canonicalJSON(v){
    if(v === null || v === undefined) return 'null';
    if(typeof v == 'number') return isFinite(v) ? JSON.stringify(v) : 'null';
    if(typeof v == 'string' || typeof v == 'boolean') return JSON.stringify(v);
    if(Array.isArray(v)){
        return '[' + v.map(function(x){
            return (x === undefined || typeof x == 'function') ? 'null' : canonicalJSON(x);
        }).join(',') + ']';
    }
    if(typeof v == 'object'){
        if(typeof v.toJSON == 'function') return canonicalJSON(v.toJSON());
        var keys = Object.keys(v).sort();
        var out = [];
        keys.forEach(function(k){
            var x = v[k];
            if(x === undefined || typeof x == 'function') return;
            out.push(JSON.stringify(k) + ':' + canonicalJSON(x));
        });
        return '{' + out.join(',') + '}';
    }
    return 'null';
}

// What verification compares: the document without its blobs, plus each blob's sha256
// (or null). Blob hashes are passed in because hashing differs between node, the browser
// and goja.
function canonicalGame(doc, blobHashes){
    var main = {};
    Object.keys(doc).forEach(function(k){
        if(BLOBS.indexOf(k) < 0) main[k] = doc[k];
    });
    blobHashes = blobHashes || {};
    return canonicalJSON({
        doc: main,
        fullState: blobHashes.fullState || null,
        replayInfo: blobHashes.replayInfo || null
    });
}

function boxRefs(cards){
    var seen = {};
    if(Array.isArray(cards)){
        cards.forEach(function(c){
            if(!c || typeof c.id != 'string') return;
            var m = c.id.match(BOX_PREFIX);
            if(m) seen[m[1]] = true;
        });
    }
    return Object.keys(seen).sort();
}

// Derived columns on stcc_games, for sorting, filtering and indexes. Copies, not the data.
function summaryColumns(summary){
    return {
        startDate: typeof summary.startDate == 'number' ? summary.startDate : 0,
        endDate: typeof summary.endDate == 'number' ? summary.endDate : 0,
        hostUsername: typeof summary.hostUsername == 'string' ? summary.hostUsername : '',
        practice: !!summary.practice,
        campaign: !!summary.campaign
    };
}

// The bytes of a compressed blob, whatever shape it arrived in, or null for "no blob".
// Not used inside PocketBase (goja has no atob/Buffer); node and the browser only.
//   Uint8Array / ArrayBuffer
//   a Firestore Blob or Bytes (toUint8Array)
//   {$bytes: base64}                          – how migrate.mjs writes Bytes to a backup
//   {_delegate: {_byteString: {binaryString}}} – a Firestore Blob after an IndexedDB round trip
//   {_byteString: {binaryString}}             – the same, one level down
function blobBytes(v){
    if(v === null || v === undefined || v === false) return null;
    if(typeof Uint8Array != 'undefined' && v instanceof Uint8Array) return v;
    if(typeof ArrayBuffer != 'undefined' && v instanceof ArrayBuffer) return new Uint8Array(v);
    if(typeof v.toUint8Array == 'function') return v.toUint8Array();
    if(typeof v.$bytes == 'string') return base64ToBytes(v.$bytes);
    if(v._delegate) return blobBytes(v._delegate);
    if(v._byteString && typeof v._byteString.binaryString == 'string'){
        var s = v._byteString.binaryString;
        var u8 = new Uint8Array(s.length);
        for(var i = 0; i < s.length; i++){
            var c = s.charCodeAt(i);
            if(c > 255) throw new Error('blob binaryString is not latin1');
            u8[i] = c;
        }
        return u8;
    }
    throw new Error('unrecognised blob shape: ' + Object.keys(v).join(','));
}

function base64ToBytes(b64){
    if(typeof Buffer != 'undefined') return new Uint8Array(Buffer.from(b64, 'base64'));
    var s = atob(b64);
    var u8 = new Uint8Array(s.length);
    for(var i = 0; i < s.length; i++) u8[i] = s.charCodeAt(i);
    return u8;
}

module.exports = {
    HEAVY: HEAVY,
    BLOBS: BLOBS,
    checkKey: checkKey,
    split: split,
    reassemble: reassemble,
    canonicalJSON: canonicalJSON,
    canonicalGame: canonicalGame,
    boxRefs: boxRefs,
    summaryColumns: summaryColumns,
    blobBytes: blobBytes
};
